import type { FC, ReactNode } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Container, Link, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import TerminalIcon from '@mui/icons-material/Terminal'
import { containerSx, inlineLinkSx, titleSx } from '../styles'

interface HelpLink {
  readonly title: string
  readonly description: string
  readonly path: string
  readonly icon: ReactNode
}

const HELP_LINKS: readonly HelpLink[] = [
  {
    title: 'Security Model',
    description: 'E2E encryption architecture, key management, and zero-trust design',
    path: '/docs/security',
    icon: <LockOutlinedIcon />,
  },
  {
    title: 'Onboarding',
    description: 'Generate your key ring, API key, and register your first host',
    path: '/docs/onboarding',
    icon: <PersonAddOutlinedIcon />,
  },
  {
    title: 'Daemon Setup',
    description: 'Install and configure the daemon on your machine',
    path: '/docs/daemon-setup',
    icon: <SettingsIcon />,
  },
  {
    title: 'Using the Daemon',
    description: 'Interactive mode commands for sessions, features, and encryption',
    path: '/docs/daemon-usage',
    icon: <TerminalIcon />,
  },
  {
    title: 'FAQ',
    description: 'Common questions about security, architecture, and how things work',
    path: '/docs/faq',
    icon: <HelpOutlineIcon />,
  },
]

export const HelpIndexPage: FC = () => {
  return (
    <Container maxWidth="md" sx={containerSx}>
      <Box component="nav" sx={topNavSx} aria-label="Breadcrumb">
        <Link component={RouterLink} to="/" sx={inlineLinkSx}>
          Home
        </Link>
        <Typography component="span" sx={navDotSx}>
          &middot;
        </Typography>
        <Link component={RouterLink} to="/hosts" sx={inlineLinkSx}>
          Dashboard
        </Link>
      </Box>

      <Typography variant="h5" sx={titleSx}>
        Help & Documentation
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={introSx}>
        Guides and documentation to help you get the most out of the Claude Code Control Center.
      </Typography>

      <Box component="ul" sx={linkListSx}>
        {HELP_LINKS.map((link, index) => (
          <Box key={link.path} component="li" sx={linkItemSx(index < HELP_LINKS.length - 1)}>
            <Link component={RouterLink} to={link.path} sx={linkRowSx} underline="none">
              <Box sx={linkIconSx}>{link.icon}</Box>
              <Box sx={linkContentSx}>
                <Typography variant="body1" sx={linkTitleSx}>
                  {link.title}
                </Typography>
                <Typography variant="body2" sx={linkDescriptionSx}>
                  {link.description}
                </Typography>
              </Box>
            </Link>
          </Box>
        ))}
      </Box>
    </Container>
  )
}

const topNavSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 4,
}

const navDotSx: SxProps<Theme> = {
  color: 'text.disabled',
  fontSize: '0.875rem',
  lineHeight: 1,
  userSelect: 'none',
}

const introSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  mb: 4,
}

const linkListSx: SxProps<Theme> = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
}

const linkItemSx = (hasBorder: boolean): SxProps<Theme> => ({
  ...(hasBorder && {
    borderBottom: 1,
    borderColor: 'divider',
  }),
})

const linkRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  py: 2,
  px: 1,
  borderRadius: 1,
  color: 'inherit',
  transition: 'background-color 150ms ease-in-out',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px',
  },
}

const linkIconSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  mt: 0.25,
  '& .MuiSvgIcon-root': {
    fontSize: 22,
    color: 'text.secondary',
  },
}

const linkContentSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
}

const linkTitleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.95rem',
  color: 'text.primary',
  mb: 0.25,
}

const linkDescriptionSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.8125rem',
  lineHeight: 1.5,
}
